<template>
  <van-popup
    v-model="popupVisible"
    class="project-picker-popup"
    position="bottom"
    round
    :close-on-click-overlay="false"
  >
    <section class="project-picker" aria-label="选择项目">
      <header class="picker-header">
        <strong>{{ title }}</strong>
        <button type="button" aria-label="关闭项目选择" @click="handleCancel">×</button>
      </header>

      <div class="project-list">
        <ul class="project-grid">
          <li
            v-for="project in projects"
            :key="project.id"
            class="project-card"
            :class="{ selected: internalSelectedId === project.id }"
            @click="toggleSelection(project.id)"
          >
            <strong>{{ project.name }}</strong>
            <span>￥{{ project.price }}/{{ project.unit }}</span>
          </li>
        </ul>
      </div>

      <footer class="picker-footer">
        <button type="button" class="cancel-button" @click="handleCancel">取消</button>
        <button
          type="button"
          class="confirm-button"
          :disabled="!selectedProject"
          @click="handleConfirm"
        >
          确认
        </button>
      </footer>
    </section>
  </van-popup>
</template>

<script>
export default {
  name: "ProjectSelectPopup",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "选择项目",
    },
    projects: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      internalSelectedId: null,
    };
  },
  computed: {
    /**
     * 将组件的 v-model 映射为 van-popup 所需的双向属性。
     * setter 通过 input 事件通知父页面更新显示状态，使组件兼容 Vue 2 标准 v-model。
     */
    popupVisible: {
      get() {
        return this.value;
      },
      set(visible) {
        this.$emit("input", visible);
      },
    },
    /**
     * 根据内部临时 id 查找完整项目。
     * 弹窗确认前不直接修改父页面数据，取消弹窗时不会污染上一次确认结果。
     */
    selectedProject() {
      return (
        this.projects.find(
          (project) => project.id === this.internalSelectedId
        ) || null
      );
    },
  },
  watch: {
    /**
     * 每次打开弹窗时使用父页面已确认的 selectedId 回显选中态。
     * 关闭时不回写临时选择，下次打开仍以父页面的正式值为准。
     */
    value(visible) {
      if (visible) {
        this.internalSelectedId = this.selectedId;
      }
    },
  },
  methods: {
    /**
     * 切换项目单选状态。
     * 点击新项目会覆盖旧 id，保证最多选中一个；再次点击当前项目会清空 id。
     *
     * @param {string|number} projectId 项目的唯一标识。
     */
    toggleSelection(projectId) {
      this.internalSelectedId =
        this.internalSelectedId === projectId ? null : projectId;
    },
    /**
     * 确认当前选择。
     * 向父页面返回完整项目对象，再关闭弹窗；未选中项目时不执行任何操作。
     */
    handleConfirm() {
      if (!this.selectedProject) {
        return;
      }
      this.$emit("confirm", { ...this.selectedProject });
      this.popupVisible = false;
    },
    /**
     * 取消选择并关闭弹窗。
     * cancel 事件可供父页面执行额外清理，临时选择不会被确认或写回。
     */
    handleCancel() {
      this.internalSelectedId = null;
      this.$emit("cancel");
      this.popupVisible = false;
    },
  },
};
</script>

<style scoped lang="scss">
.project-picker-popup {
  max-height: 78vh;
  overflow: hidden;
  background: #fffdfa;
}

.project-picker {
  display: flex;
  max-height: 78vh;
  min-height: 440px;
  flex-direction: column;
  background: #fffdfa;
}

.picker-header {
  display: flex;
  height: 86px;
  padding: 0 26px;
  flex: 0 0 86px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee5d8;
  box-sizing: border-box;

  strong {
    font-size: 29px;
    font-weight: 600;
  }

  button {
    width: 50px;
    height: 50px;
    padding: 0;
    border: 0;
    color: #8c8377;
    background: transparent;
    font-size: 42px;
  }
}

.project-list {
  min-height: 0;
  padding: 26px 28px;
  flex: 1;
  overflow-y: auto;
}

.project-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 46px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.project-card {
  display: flex;
  height: 120px;
  min-width: 0;
  padding: 20px 22px;
  flex-direction: column;
  justify-content: center;
  border: 2px solid #e3dfd6;
  border-radius: 14px;
  box-sizing: border-box;
  background: #fff;

  strong {
    overflow: hidden;
    font-size: 27px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    margin-top: 10px;
    overflow: hidden;
    font-size: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.selected {
    border-color: #f0525d;
    background: #fffafa;
    box-shadow: 0 0 0 1px rgba(240, 82, 93, 0.12);
  }
}

.picker-footer {
  display: flex;
  height: calc(102px + env(safe-area-inset-bottom));
  padding: 12px 24px env(safe-area-inset-bottom);
  flex: 0 0 auto;
  justify-content: space-between;
  border-top: 1px solid #eadfce;
  box-sizing: border-box;

  button {
    width: 335px;
    height: 72px;
    border-radius: 36px;
    font-size: 28px;
  }
}

.cancel-button {
  border: 1px solid #ded7ca;
  color: #a98e59;
  background: #faf7f1;
}

.confirm-button {
  border: 0;
  color: #fff;
  background: linear-gradient(90deg, #d0ad68, #f0c875);

  &:disabled {
    color: #c7bda9;
    background: #e8e2d8;
  }
}
</style>
